import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, DestroyRef, effect, Inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import * as papa from 'papaparse';

import { MacSerialNumber } from '@shure/cloud/shared/models/devices';
import { FileSaverService } from '@shure/cloud/shared/services/file-saver';
import { BreakpointService } from '@shure/cloud/shared/services/media-breakpoints';
import { CloudFeatureFlagsService, FEATURE_FLAG_SERVICE } from '@shure/cloud/shared/utils/feature-flags';
import {
	PrismFileInput,
	PrismFileInputComponent,
	PrismIconComponent,
	PrismRadioButtonComponent,
	PrismRadioGroupComponent,
	PrismSnackbarService,
	TextValueAccessor
} from '@shure/prism-angular-components';
import {
	getSerialNumberValidators,
	macAddressRegEx,
	serialNumberRegEx
} from '@shure/shared/angular/ui/validators/common';

import { AddDevicesDialogService } from '../../../../services/add-devices-dialog.service';

import { Step1HowToFindConfig } from './step1-how-to-find.config';

type DeviceDiscoveryMethods = 'LocalNetwork' | 'MacSerialSingleDevice' | 'MacSerialBatchFile' | 'QrCode';

const csvMimeTypes = ['text/csv', 'application/vnd.ms-excel', 'application/csv', 'text/plain'];

export type MacSerialNumberAnnotated = MacSerialNumber & {
	comment?: string;
};

export type MacSerialNumberValidationResults = {
	numErrors: number;
	numDataRows: number;
	data: MacSerialNumberAnnotated[];
};

@Component({
	selector: 'sh-step1-how-to-find',
	standalone: true,
	imports: [
		CommonModule,
		MatCardModule,
		TranslocoModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		NgxMaskDirective,
		PrismFileInputComponent,
		PrismIconComponent,
		PrismRadioGroupComponent,
		PrismRadioButtonComponent,
		TextValueAccessor
	],
	providers: [provideNgxMask()],
	templateUrl: './step1-how-to-find.component.html',
	styleUrls: ['./step1-how-to-find.component.scss']
})
export class Step1HowToFindComponent {
	@ViewChild('fileInput') private fileInput!: PrismFileInput;

	protected templateUrl = 'assets/files/mac-serial-claiming.template.csv';
	protected templateSaveName = 'mac-serialnumber-csv-template.csv';
	protected customMacSerialPattern = { A: { pattern: new RegExp('[0-9a-fA-F]') } };
	public currentDiscoveryMethod: DeviceDiscoveryMethods | null = null;
	public batchFileValidationResults: MacSerialNumberValidationResults | null = null;

	// The form group containing the user's selection of either 'loalNetwor', 'Single Mac Serial', or a Mac Serial Batch file.
	public discoveryMethodFormGroup = new FormGroup({
		// by default, the MacSerialBatchFile option is selected
		discoveryMethod: new FormControl<DeviceDiscoveryMethods | null>(null, [Validators.required])
	});

	public macSerialBatchFileFormGroup = new FormGroup({
		fileName: new FormControl<string>({ value: '', disabled: true }, [Validators.required]),
		contents: new FormControl<MacSerialNumber[]>({ value: [], disabled: true }, [
			Validators.required,
			Validators.minLength(1)
		])
	});

	public macSerialSingleDeviceFormGroup = new FormGroup({
		macAddress: new FormControl({ value: '', disabled: true }, { validators: Validators.required }),
		serialNumber: new FormControl(
			{ value: '', disabled: true },
			{ validators: getSerialNumberValidators(), updateOn: 'change' }
		)
	});

	public localNetworkFormGroup = new FormGroup({});
	public qrCodeFormGroup = new FormGroup({});
	public serialNumberFocused = signal(false);
	public readonly isSmallDevice = this.breakpointService.isLteSmall;
	public readonly isQrCodeClaimingEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-qr-code-claiming')
	);

	constructor(
		protected readonly addDevicesDialogService: AddDevicesDialogService,
		private readonly httpClient: HttpClient,
		private readonly snackbarService: PrismSnackbarService,
		private readonly fileSaverService: FileSaverService,
		private readonly translocoService: TranslocoService,
		private readonly breakpointService: BreakpointService,
		private readonly destroyRef: DestroyRef,
		private readonly changeDetectorRef: ChangeDetectorRef,
		stepConfig: Step1HowToFindConfig,
		@Inject(FEATURE_FLAG_SERVICE) private readonly featureFlagsService: CloudFeatureFlagsService
	) {
		this.localNetworkFormGroup.disable();
		this.localNetworkFormGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
			const valid = this.localNetworkFormGroup.valid;
			if (valid) {
				this.addDevicesDialogService.setClaimByTokenLocalNetwork();
			}
			stepConfig.isValid.set(valid);
		});

		this.macSerialSingleDeviceFormGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe((newValue) => {
			const valid = this.macSerialSingleDeviceFormGroup.valid;
			if (valid) {
				this.addDevicesDialogService.setClaimByMacSerialNumberSingle([
					{
						macAddress: newValue.macAddress?.toUpperCase() ?? '',
						serialNumber: newValue.serialNumber?.toUpperCase() ?? ''
					}
				]);
			}
			stepConfig.isValid.set(valid);
		});

		this.macSerialBatchFileFormGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
			const valid = this.macSerialBatchFileFormGroup.valid;
			if (valid) {
				this.addDevicesDialogService.setClaimByMacSerialNumberCsv(
					this.macSerialBatchFileFormGroup.get('contents')?.value ?? []
				);
			}
			stepConfig.isValid.set(this.macSerialBatchFileFormGroup.valid);
		});

		this.qrCodeFormGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
			const valid = this.qrCodeFormGroup.valid;
			if (valid) {
				this.addDevicesDialogService.setClaimByQrCode();
			}
			stepConfig.isValid.set(valid);
		});

		effect(() => {
			// by default, the QrCode option is selected on small devices and the MacSerialBatchFile option is
			// selected on other devices
			const isSmallDevice = this.isSmallDevice();
			const isQrCodeClaimingEnabled = this.isQrCodeClaimingEnabled();
			if (isSmallDevice && isQrCodeClaimingEnabled) {
				this.onDeviceDiscoveryMethodSelection({ detail: 'QrCode' });
			} else {
				this.onDeviceDiscoveryMethodSelection({ detail: 'MacSerialBatchFile' });
			}
		});
	}

	public onSerialNumberFocus(): void {
		this.macSerialSingleDeviceFormGroup.get('serialNumber')?.setErrors(null);
		this.macSerialSingleDeviceFormGroup.get('serialNumber')?.markAsPristine();
		this.macSerialSingleDeviceFormGroup.get('serialNumber')?.markAsUntouched();
		this.serialNumberFocused.set(true);
	}

	public onSerialNumberBlur(): void {
		this.macSerialSingleDeviceFormGroup.get('serialNumber')?.updateValueAndValidity();
		this.serialNumberFocused.set(false);
	}

	// function to handle when user selects a different method to discover devices.
	// This function will disable the prior selected method, then enable the new method.
	// I'm relying on the `valueChanges` subscription setup in the constructor to
	// more fully react to specific changes for a given discovery method.
	public onDeviceDiscoveryMethodSelection(event: Partial<CustomEvent<string | undefined>>): void {
		const newSelection = event.detail;
		if (
			newSelection !== 'LocalNetwork' &&
			newSelection !== 'MacSerialSingleDevice' &&
			newSelection !== 'MacSerialBatchFile' &&
			newSelection !== 'QrCode'
		) {
			// occurs when user has clicked on a radio button, then clicks elsewhere on the page.
			return;
		}

		// disable previous selection
		if (this.currentDiscoveryMethod !== null) {
			switch (this.currentDiscoveryMethod) {
				case 'LocalNetwork':
					this.localNetworkFormGroup.disable();
					break;
				case 'MacSerialSingleDevice':
					this.macSerialSingleDeviceFormGroup.disable();
					break;
				case 'MacSerialBatchFile':
					this.macSerialBatchFileFormGroup.reset();
					this.fileInput.value = '';
					this.macSerialBatchFileFormGroup.disable();
					break;
				case 'QrCode':
					this.qrCodeFormGroup.disable();
					break;
			}
		}

		// enable the formGroup for the new selection.
		this.currentDiscoveryMethod = newSelection;
		this.discoveryMethodFormGroup.setValue({ discoveryMethod: newSelection });
		switch (this.currentDiscoveryMethod) {
			case 'LocalNetwork':
				this.localNetworkFormGroup.enable();
				break;
			case 'MacSerialSingleDevice':
				this.macSerialSingleDeviceFormGroup.enable();
				break;
			case 'MacSerialBatchFile':
				this.macSerialBatchFileFormGroup.enable();
				break;
			case 'QrCode':
				this.qrCodeFormGroup.enable();
				break;
		}
	}

	/**
	 *
	 * @param event A file input event from a user selecting a file from the file chooser
	 * @returns a promise if the file couldbe parsed or not.
	 */
	public onFileSelected(event: CustomEvent<File | File[]>): Promise<string> {
		const file = <File>event.detail;
		return new Promise((resolve) => {
			// assuming this function will not get invoked unless exactly one file is selected.
			this.batchFileValidationResults = null;

			// to prevent the user from trying to throw a garbage file at the dialog, make sure the
			// file has a .csv suffix and it has a legit MIME type.
			if (!file.name.endsWith('csv') || !csvMimeTypes.includes(file.type)) {
				const errString = this.translocoService.translate(
					'cloud.add-devices-dialog.mac-serial-csv-file-not-csv-file'
				);
				this.setFormGroupErrors(file.name, errString);
				resolve(errString);
				return;
			}

			const fileName = file.name;

			papa.parse<[string, string]>(file, {
				delimiter: ',',
				skipEmptyLines: true,
				header: false, // if true is used, the indexing of the result is by column heading,
				// which I don't want in case a user changes the headers. By using
				// false, the indexing is by offset: data[rec#][0], data[rec#][1]
				encoding: 'utf-8',
				complete: (results) => {
					if (results.errors.length > 0) {
						const errString = this.translocoService.translate(
							'cloud.add-devices-dialog.mac-serial-csv-file-parse-error'
						);
						this.setFormGroupErrors(fileName, errString);
						resolve(errString);
						return;
					}

					if (results.data.length < 1) {
						const errString = this.translocoService.translate(
							'cloud.add-devices-dialog.mac-serial-csv-file-empty'
						);
						this.setFormGroupErrors(fileName, errString);
						resolve(errString);
						return;
					}

					this.batchFileValidationResults = this.validateMacSerialData(results.data);
					if (this.batchFileValidationResults.numDataRows === 0) {
						const errString = this.translocoService.translate(
							'cloud.add-devices-dialog.mac-serial-csv-file-no-data'
						);
						this.setFormGroupErrors(fileName, errString);
						resolve(errString);
						return;
					}

					if (this.batchFileValidationResults.numErrors !== 0) {
						const errString = this.translocoService.translate(
							'cloud.add-devices-dialog.mac-serial-csv-file-syntax-errors'
						);
						this.setFormGroupErrors(fileName, errString);
						resolve(errString);
						return;
					}

					// all's good. Set the value in the form group. We'll let the change
					// detection result in storing the value in the service.
					this.macSerialBatchFileFormGroup.setValue({
						fileName: fileName,
						contents: this.batchFileValidationResults.data
					});
					this.macSerialBatchFileFormGroup.controls.fileName.setErrors(null);
					resolve('Success');
				},
				error: (_error) => {
					const errString = this.translocoService.translate(
						'cloud.add-devices-dialog.mac-serial-csv-file-parse-error'
					);
					this.setFormGroupErrors(fileName, errString);
					resolve(errString);
					return;
				}
			});
		});
	}

	public saveCSVTemplateFile(templateUrl: string, templateSaveName: string): void {
		this.httpClient
			.get(templateUrl, {
				responseType: 'text'
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (fileContents) => {
					this.fileSaverService.saveAsCSV(templateSaveName, fileContents);
					this.snackbarService.open({
						message: this.translocoService.translate(
							'cloud.add-devices-dialog.csv-file-template-save-success'
						),
						actionText: this.translocoService.translate('common.snackbar.dismiss')
					});
				},
				error: () => {
					this.snackbarService.open({
						message: this.translocoService.translate(
							'cloud.add-devices-dialog.csv-file-template-save-error'
						),
						actionText: this.translocoService.translate('common.snackbar.dismiss')
					});
				}
			});
	}

	public validateMacSerialData(data: [string, string][]): MacSerialNumberValidationResults {
		const retVal: MacSerialNumberValidationResults = {
			numErrors: 0,
			numDataRows: 0,
			data: []
		};

		data = data.slice(1); // skip over the header row
		data.forEach((item) => {
			++retVal.numDataRows;
			const macAddress = item[0] ? item[0].trim().toUpperCase() : '';
			const serialNumber = item[1] ? item[1].trim().toUpperCase() : '';
			let comment = '';

			// make sure macAddress and serialNumber conform to regex.
			if (!macAddress.match(macAddressRegEx)) {
				++retVal.numErrors;
				comment += this.translocoService.translate(
					'cloud.add-devices-dialog.mac-serial-csv-file-mac-address-syntax-error'
				);
			}

			if (!serialNumber.match(serialNumberRegEx)) {
				++retVal.numErrors;
				if (comment !== '') comment += ', ';
				comment += this.translocoService.translate(
					'cloud.add-devices-dialog.mac-serial-csv-file-serial-number-syntax-error'
				);
			}

			// check for duplicates
			const existingItem = retVal.data.findIndex(
				(item) => item.macAddress === macAddress && item.serialNumber === serialNumber
			);
			if (existingItem !== -1) {
				if (comment !== '') comment += ', ';
				comment += this.translocoService.translate(
					'cloud.add-devices-dialog.mac-serial-csv-file-duplicate', //
					{ m: existingItem + 2 } // 2 - to account for 0 indexing, and presence of header row.
				);
				++retVal.numErrors;
			}

			retVal.data.push({
				macAddress: macAddress,
				serialNumber: serialNumber,
				comment: comment
			});
		});

		return retVal;
	}

	public saveBatchFileWithErrors(): void {
		let csvWithComments = this.translocoService.translate(
			'cloud.add-devices-dialog.mac-serial-csv-error-file-header'
		);
		csvWithComments += '\n';
		csvWithComments += this.batchFileValidationResults?.data
			.map((item) => `"${item.macAddress}","${item.serialNumber}","${item.comment ?? ''}"`)
			.join('\n');

		this.fileSaverService.saveAsCSV('mac-serial-batch-file.errors.csv', csvWithComments);

		this.snackbarService.open({
			message: this.translocoService.translate('cloud.add-devices-dialog.mac-serial-csv-file-syntax-errors'),
			actionText: this.translocoService.translate('common.snackbar.dismiss')
		});
	}

	private setFormGroupErrors(fileName: string, error: string | null): void {
		this.macSerialBatchFileFormGroup.setValue({
			fileName: fileName,
			contents: []
		});
		this.macSerialBatchFileFormGroup.controls.fileName.setErrors({
			error: error
		});

		this.changeDetectorRef.detectChanges();
	}
}
